import type { QueryClient, QueryKey } from "@tanstack/react-query";

import {
  type AgendaItem,
  type Encounter,
  getGetEncounterByIdQueryKey,
  getGetPatientByIdQueryKey,
  getListAgendaItemsQueryKey,
  getListEncountersQueryKey,
  getListMedicalCodesByEncounterQueryKey,
  getSearchPatientsQueryKey,
} from "@elation/api-client/src/stitch-web";
import type { ListEncountersResponse } from "@elation/api-client/src/stitch-web/models/listEncountersResponse";
import type { PatientSearchResult } from "@elation/api-client/src/stitch-web/models/patientSearchResult";

import type { TourStepAction } from "../../ui";
import {
  VISIT_TOUR_ENCOUNTER_ID,
  VISIT_TOUR_PATIENT_ID,
  VISIT_TOUR_PATIENT_SEARCH_PARAMS,
  VISIT_TOUR_PATIENT_SELECTOR_PARAMS,
  VISIT_TOUR_PHYSICIAN_ID,
  VISIT_TOUR_PRACTICE_ID,
  VISIT_TOUR_RECENT_VISITS_PARAMS,
  visitTourAgendaItems,
  visitTourCompletedEncounter,
  visitTourEncounter,
  visitTourEncounterListItem,
  visitTourEncountersResponse,
  visitTourMedicalCodes,
  visitTourPatient,
  visitTourPatientSearchItem,
  visitTourPatientSearchResult,
  visitTourRecordingEncounter,
} from "./visit-tour.fixtures";

type CachedAgendaItemList = {
  response?: unknown;
  data: { items: AgendaItem[] };
};

type CachedData<T> = {
  response?: unknown;
  data: T;
};

const tourDetailQueryKeys = [
  getGetPatientByIdQueryKey(VISIT_TOUR_PATIENT_ID),
  getGetEncounterByIdQueryKey(VISIT_TOUR_ENCOUNTER_ID),
  getListAgendaItemsQueryKey(VISIT_TOUR_ENCOUNTER_ID),
  getListMedicalCodesByEncounterQueryKey(VISIT_TOUR_ENCOUNTER_ID),
] satisfies QueryKey[];

const tourListQueryKeys = [
  getListEncountersQueryKey(VISIT_TOUR_RECENT_VISITS_PARAMS),
  getSearchPatientsQueryKey(VISIT_TOUR_PATIENT_SEARCH_PARAMS),
  getSearchPatientsQueryKey(VISIT_TOUR_PATIENT_SELECTOR_PARAMS),
] satisfies QueryKey[];

const tourQueryKeys = [...tourDetailQueryKeys, ...tourListQueryKeys];
const VISIT_TOUR_AGENDA_REVEAL_INITIAL_DELAY_MS = 350;
const VISIT_TOUR_AGENDA_REVEAL_INTERVAL_MS = 450;
const VISIT_TOUR_AGENDA_CHECK_INTERVAL_MS = 1_200;
const VISIT_TOUR_COMPLETION_DELAY_MS = 900;
type QueryDefaults = Parameters<QueryClient["setQueryDefaults"]>[1];
const tourQueryDefaultSnapshots = new Map<string, QueryDefaults | undefined>();
let visitTourRunId = 0;

function wait(ms: number) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function queryKeySnapshotKey(queryKey: QueryKey): string {
  return JSON.stringify(queryKey);
}

function applyVisitTourQueryDefaults(queryClient: QueryClient) {
  for (const queryKey of tourQueryKeys) {
    const snapshotKey = queryKeySnapshotKey(queryKey);
    if (!tourQueryDefaultSnapshots.has(snapshotKey)) {
      tourQueryDefaultSnapshots.set(
        snapshotKey,
        queryClient.getQueryDefaults(queryKey),
      );
    }

    queryClient.setQueryDefaults(queryKey, {
      gcTime: 30 * 60 * 1000,
      refetchOnMount: false,
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: Infinity,
    });
  }
}

function restoreVisitTourQueryDefaults(queryClient: QueryClient) {
  for (const queryKey of tourQueryKeys) {
    const snapshotKey = queryKeySnapshotKey(queryKey);
    const previousDefaults = tourQueryDefaultSnapshots.get(snapshotKey);

    queryClient.setQueryDefaults(queryKey, previousDefaults ?? {});
    tourQueryDefaultSnapshots.delete(snapshotKey);
  }
}

function withoutVisitTourEncounter(
  items: ListEncountersResponse["items"],
): ListEncountersResponse["items"] {
  return items.filter((item) => item.id !== VISIT_TOUR_ENCOUNTER_ID);
}

function withoutVisitTourPatient(
  items: PatientSearchResult["items"],
): PatientSearchResult["items"] {
  return items.filter((item) => item.patientId !== VISIT_TOUR_PATIENT_ID);
}

function seedRecentVisits(queryClient: QueryClient) {
  queryClient.setQueryData<CachedData<ListEncountersResponse>>(
    getListEncountersQueryKey(VISIT_TOUR_RECENT_VISITS_PARAMS),
    (current) => {
      const data = current?.data ?? visitTourEncountersResponse;

      return {
        ...current,
        data: {
          ...data,
          items: [
            visitTourEncounterListItem,
            ...withoutVisitTourEncounter(data.items),
          ],
        },
      };
    },
  );
}

function seedPatientSearch(queryClient: QueryClient, queryKey: QueryKey) {
  queryClient.setQueryData<CachedData<PatientSearchResult>>(
    queryKey,
    (current) => {
      const data = current?.data ?? visitTourPatientSearchResult;

      return {
        ...current,
        data: {
          ...data,
          items: [
            visitTourPatientSearchItem,
            ...withoutVisitTourPatient(data.items),
          ],
        },
      };
    },
  );
}

function cleanupRecentVisits(queryClient: QueryClient) {
  queryClient.setQueryData<CachedData<ListEncountersResponse>>(
    getListEncountersQueryKey(VISIT_TOUR_RECENT_VISITS_PARAMS),
    (current) =>
      current
        ? {
            ...current,
            data: {
              ...current.data,
              items: withoutVisitTourEncounter(current.data.items),
            },
          }
        : current,
  );
}

function cleanupPatientSearch(queryClient: QueryClient, queryKey: QueryKey) {
  queryClient.setQueryData<CachedData<PatientSearchResult>>(
    queryKey,
    (current) =>
      current
        ? {
            ...current,
            data: {
              ...current.data,
              items: withoutVisitTourPatient(current.data.items),
            },
          }
        : current,
  );
}

function seedVisitTourCacheWithAgendaItems(
  queryClient: QueryClient,
  agendaItems: AgendaItem[],
) {
  visitTourRunId += 1;
  applyVisitTourQueryDefaults(queryClient);
  seedRecentVisits(queryClient);
  seedPatientSearch(
    queryClient,
    getSearchPatientsQueryKey(VISIT_TOUR_PATIENT_SEARCH_PARAMS),
  );
  seedPatientSearch(
    queryClient,
    getSearchPatientsQueryKey(VISIT_TOUR_PATIENT_SELECTOR_PARAMS),
  );

  queryClient.setQueryData(getGetPatientByIdQueryKey(VISIT_TOUR_PATIENT_ID), {
    data: visitTourPatient,
  });
  queryClient.setQueryData(
    getGetEncounterByIdQueryKey(VISIT_TOUR_ENCOUNTER_ID),
    {
      data: visitTourEncounter,
    },
  );
  queryClient.setQueryData(
    getListAgendaItemsQueryKey(VISIT_TOUR_ENCOUNTER_ID),
    {
      data: { items: agendaItems },
    },
  );
  queryClient.setQueryData(
    getListMedicalCodesByEncounterQueryKey(VISIT_TOUR_ENCOUNTER_ID),
    {
      data: visitTourMedicalCodes,
    },
  );
}

export function seedVisitTourCache(queryClient: QueryClient) {
  seedVisitTourCacheWithAgendaItems(queryClient, visitTourAgendaItems);
}

export function seedVisitTourPatientCache(queryClient: QueryClient) {
  seedVisitTourCacheWithAgendaItems(queryClient, []);
}

export function cleanupVisitTourCache(queryClient: QueryClient) {
  visitTourRunId += 1;
  restoreVisitTourQueryDefaults(queryClient);
  cleanupRecentVisits(queryClient);
  cleanupPatientSearch(
    queryClient,
    getSearchPatientsQueryKey(VISIT_TOUR_PATIENT_SEARCH_PARAMS),
  );
  cleanupPatientSearch(
    queryClient,
    getSearchPatientsQueryKey(VISIT_TOUR_PATIENT_SELECTOR_PARAMS),
  );

  for (const queryKey of tourDetailQueryKeys) {
    queryClient.removeQueries({ exact: true, queryKey });
  }
}

export function resetVisitTourRecording(queryClient: QueryClient) {
  visitTourRunId += 1;
  updateVisitTourEncounter(queryClient, visitTourEncounter);
  updateVisitTourAgendaItems(queryClient, (items) =>
    items.map((item) => ({
      ...item,
      status: "open",
      signalState: "open",
      completedAt: null,
      completedBy: undefined,
      discussedAt: null,
      assessedAt: null,
      plannedAt: null,
      followUpAt: null,
      updatedAt: new Date().toISOString(),
    })),
  );
}

export async function revealVisitTourAgendaItems(
  queryClient: QueryClient,
): Promise<boolean> {
  const runId = visitTourRunId;
  const isCurrentRun = () => runId === visitTourRunId;

  await wait(VISIT_TOUR_AGENDA_REVEAL_INITIAL_DELAY_MS);
  if (!isCurrentRun()) return false;

  for (const item of visitTourAgendaItems) {
    updateVisitTourAgendaItems(queryClient, (items) =>
      items.some((existingItem) => existingItem.id === item.id)
        ? items
        : [...items, item],
    );

    await wait(VISIT_TOUR_AGENDA_REVEAL_INTERVAL_MS);
    if (!isCurrentRun()) return false;
  }

  return true;
}

export function completeVisitTourCache(queryClient: QueryClient) {
  queryClient.setQueryData(
    getGetEncounterByIdQueryKey(VISIT_TOUR_ENCOUNTER_ID),
    {
      data: visitTourCompletedEncounter,
    },
  );
  queryClient.setQueryData(
    getListMedicalCodesByEncounterQueryKey(VISIT_TOUR_ENCOUNTER_ID),
    {
      data: visitTourMedicalCodes,
    },
  );
}

function updateVisitTourEncounter(
  queryClient: QueryClient,
  encounter: Encounter,
) {
  queryClient.setQueryData(
    getGetEncounterByIdQueryKey(VISIT_TOUR_ENCOUNTER_ID),
    {
      data: encounter,
    },
  );
}

function updateVisitTourAgendaItems(
  queryClient: QueryClient,
  updater: (items: AgendaItem[]) => AgendaItem[],
) {
  const queryKey = getListAgendaItemsQueryKey(VISIT_TOUR_ENCOUNTER_ID);
  queryClient.setQueryData<CachedAgendaItemList>(queryKey, (current) => {
    const items = current?.data.items ?? visitTourAgendaItems;

    return {
      ...current,
      data: {
        items: updater(items),
      },
    };
  });
}

function markVisitTourAgendaItemPlanned(
  queryClient: QueryClient,
  itemId: number,
) {
  const now = new Date().toISOString();

  updateVisitTourAgendaItems(queryClient, (items) =>
    items.map((item) =>
      item.id === itemId
        ? {
            ...item,
            status: "finalized",
            signalState: "planned",
            completedAt: now,
            completedBy: "ai_agent",
            discussedAt: now,
            assessedAt: now,
            plannedAt: now,
            updatedAt: now,
          }
        : item,
    ),
  );
}

export async function startVisitTourRecording(
  queryClient: QueryClient,
): Promise<boolean> {
  visitTourRunId += 1;
  const runId = visitTourRunId;
  const isCurrentRun = () => runId === visitTourRunId;

  updateVisitTourEncounter(queryClient, visitTourRecordingEncounter);

  const queryKey = getListAgendaItemsQueryKey(VISIT_TOUR_ENCOUNTER_ID);
  const agendaItems =
    queryClient.getQueryData<CachedAgendaItemList>(queryKey)?.data.items ??
    visitTourAgendaItems;

  for (const item of agendaItems.filter(
    (item) => item.signalState !== "planned",
  )) {
    await wait(VISIT_TOUR_AGENDA_CHECK_INTERVAL_MS);
    if (!isCurrentRun()) return false;
    markVisitTourAgendaItemPlanned(queryClient, item.id);
  }

  await wait(VISIT_TOUR_COMPLETION_DELAY_MS);
  if (!isCurrentRun()) return false;
  completeVisitTourCache(queryClient);
  return true;
}

export function addVisitTourAgendaItem(
  queryClient: QueryClient,
  description: string,
) {
  const queryKey = getListAgendaItemsQueryKey(VISIT_TOUR_ENCOUNTER_ID);
  const now = new Date().toISOString();

  queryClient.setQueryData<CachedAgendaItemList>(queryKey, (current) => {
    const items = current?.data.items ?? visitTourAgendaItems;
    const nextId = Math.min(0, ...items.map((item) => item.id)) - 1;
    const item: AgendaItem = {
      id: nextId,
      description,
      type: "custom",
      status: "open",
      signalState: "open",
      patientId: VISIT_TOUR_PATIENT_ID,
      practiceId: VISIT_TOUR_PRACTICE_ID,
      encounterId: VISIT_TOUR_ENCOUNTER_ID,
      sortOrder: items.length,
      careProtocolOpportunityId: null,
      createdByPhysicianId: VISIT_TOUR_PHYSICIAN_ID,
      completedAt: null,
      completedBy: undefined,
      discussedAt: null,
      assessedAt: null,
      plannedAt: null,
      followUpAt: null,
      healthConcern: null,
      medicalCodes: [],
      citations: [],
      createdAt: now,
      updatedAt: now,
    };

    return {
      ...current,
      data: {
        items: [...items, item],
      },
    };
  });
}

export const seedVisitTourCacheAction: TourStepAction = ({ queryClient }) =>
  seedVisitTourCache(queryClient);

export const seedVisitTourPatientCacheAction: TourStepAction = ({
  queryClient,
}) => seedVisitTourPatientCache(queryClient);

export const revealVisitTourAgendaItemsAction: TourStepAction = async ({
  queryClient,
}) => {
  await revealVisitTourAgendaItems(queryClient);
};

export const cleanupVisitTourCacheAction: TourStepAction = ({ queryClient }) =>
  cleanupVisitTourCache(queryClient);

export const completeVisitTourCacheAction: TourStepAction = ({ queryClient }) =>
  completeVisitTourCache(queryClient);

export const resetVisitTourRecordingAction: TourStepAction = ({
  queryClient,
}) => resetVisitTourRecording(queryClient);

export const visitTourQueryKeys = tourQueryKeys;
